import { LitElement, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { initAll } from 'nhsuk-frontend';
import { nhsHeaderTag } from '@ui/elementTags';
import { applyNhsGlobal } from '@ui/styles/applyNhsGlobal';
import headerStyles from '@ui/styles/nhs-subsets/nhs-header.scss?inline';

@customElement(nhsHeaderTag)
export class NhsHeaderComponent extends LitElement {
  @property({ type: String })
  public accountLabel = '';

  @property({ type: Boolean, attribute: 'account-link-visible' })
  public accountLinkVisible = false;

  @property({ type: String })
  public homeHref = '/journey-start';

  @property({ type: String })
  public serviceName = 'Intelligent Navigator';

  @property({ type: String })
  public helpLabel = 'NHS App Help';

  protected createRenderRoot(): HTMLElement | ShadowRoot {
    return applyNhsGlobal(this, headerStyles);
  }

  protected firstUpdated(): void {
    const headerElement = this.renderRoot.querySelector('.nhsuk-header');
    if (!headerElement || !('noModule' in HTMLScriptElement.prototype)) {
      return;
    }

    try {
      initAll(headerElement);
    } catch {
      // JSDOM does not emulate the supported-browser checks required by NHS frontend.
    }
  }

  public render() {
    const accountNavigation = this.renderAccountNavigation();

    return html`
      <header class="nhsuk-header" data-module="nhsuk-header" role="banner">
        <div class="nhsuk-header__container nhsuk-width-container">
          <div class="nhsuk-header__service">
            <a class="nhsuk-header__service-logo" href=${this.homeHref} aria-label="NHS Intelligent Navigator homepage">
              <svg class="nhsuk-header__logo" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 80" height="40" width="100" focusable="false" role="img" aria-label="NHS">
                <title>NHS</title>
                <path fill="currentcolor" d="M200 0v80H0V0h200Zm-27.5 5.5c-14.5 0-29 5-29 22 0 10.2 7.7 13.5 14.7 16.3l.7.3c5.4 2 10.1 3.9 10.1 8.4 0 6.5-8.5 7.5-14 7.5s-12.5-1.5-16-3.5L135 70c5.5 2 13.5 3.5 20 3.5 15.5 0 32-4.5 32-22.5 0-19.5-25.5-16.5-25.5-25.5 0-5.5 5.5-6.5 12.5-6.5a35 35 0 0 1 14.5 3l4-13.5c-4.5-2-12-3-20-3Zm-131 2h-22l-14 65H22l9-45h.5l13.5 45h21.5l14-65H64l-9 45h-.5l-13-45Zm63 0h-18l-13 65h17l6-28H117l-5.5 28H129l13.5-65H125L119.5 32h-20l5-24.5Z"/>
              </svg>
              <span class="nhsuk-header__service-name">${this.serviceName}</span>
            </a>
          </div>
          ${accountNavigation}
        </div>
        ${this.helpLabel
          ? html`
            <nav class="nhsuk-header__navigation" aria-label="Support">
              <div class="nhsuk-header__navigation-container nhsuk-width-container">
                <ul class="nhsuk-header__navigation-list">
                  <li class="nhsuk-header__navigation-item">
                    <a class="nhsuk-header__navigation-link" href="#" @click=${this.handleHelpClick}>${this.helpLabel}</a>
                  </li>
                  <li class="nhsuk-header__menu" hidden>
                    <button class="nhsuk-header__menu-toggle nhsuk-header__navigation-link" id="toggle-menu" aria-expanded="false">
                      <span class="nhsuk-u-visually-hidden">Browse </span>More
                    </button>
                  </li>
                </ul>
              </div>
            </nav>
          `
          : null}
      </header>
    `;
  }

  private renderAccountNavigation() {
    if (!this.accountLabel) {
      return null;
    }

    const accountContent = this.accountLinkVisible
      ? html`
        <a class="nhsuk-header__account-link" href="#" @click=${this.handleAccountClick}>
          ${this.renderUserIcon()}
          ${this.accountLabel}
        </a>
      `
      : html`
        ${this.renderUserIcon()}
        ${this.accountLabel}
      `;

    return html`
      <nav class="nhsuk-header__account" aria-label="Account">
        <ul class="nhsuk-header__account-list">
          <li class="nhsuk-header__account-item">
            ${accountContent}
          </li>
        </ul>
      </nav>
    `;
  }

  private renderUserIcon() {
    return html`
      <svg class="nhsuk-icon nhsuk-icon--user" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" focusable="false" aria-hidden="true">
        <path d="M12 1a11 11 0 1 1 0 22 11 11 0 0 1 0-22Zm0 2a9 9 0 0 0-5 16.5V18a4 4 0 0 1 4-4h2a4 4 0 0 1 4 4v1.5A9 9 0 0 0 12 3Zm0 3a3.5 3.5 0 1 1-3.5 3.5A3.4 3.4 0 0 1 12 6Z"/>
      </svg>
    `;
  }

  private readonly handleAccountClick = (event: Event): void => {
    event.preventDefault();
    this.dispatchEvent(new CustomEvent('nhs-header-account', {
      bubbles: true,
      composed: true,
    }));
  };

  private readonly handleHelpClick = (event: Event): void => {
    event.preventDefault();
    this.dispatchEvent(new CustomEvent('nhs-header-help', {
      bubbles: true,
      composed: true,
    }));
  };
}

declare global {
  interface HTMLElementTagNameMap {
    [nhsHeaderTag]: NhsHeaderComponent;
  }
}